import { UAParser } from 'ua-parser-js'
import { LocalFind, LocalInsert } from '@weblite/persist'
import { getState } from '$setup/redux'
import { SessionStorageKeys } from '$logic/client-device/types'
import getDimSelector from '../selectors/dimension'
import {
  sessionStorageGetItem,
  sessionStorageSetItem,
} from '$func/session-storage'
import { CUSTOM_SCROLL_CLASS } from '$setup/style/constants'

// NOTE: all measurements are in pixel so units are removed
const MAXIMUM_WIDTH_TO_BE_SMALL_VIEW = 640
export const MAXIMUM_WIDTH_TO_BE_MEDIUM_VIEW = 1079

export const LEFT_PANEL_DEFAULT_SIZE = 350
export const X_PANEL_DEFAULT__SIZE = 360
export const SIMULATED_PHONE_WIDTH = '400px'

export const getWindowSize = () => {
  const { width } = getDimSelector(getState()).dim.browser

  if (width <= MAXIMUM_WIDTH_TO_BE_SMALL_VIEW) return 'small'
  if (width <= MAXIMUM_WIDTH_TO_BE_MEDIUM_VIEW) return 'medium'
  return 'large'
}

const STARTUP_IS_DESKTOP_SIZE =
  window.document.body.clientWidth > MAXIMUM_WIDTH_TO_BE_MEDIUM_VIEW

export const isDesktopSize = () => {
  return STARTUP_IS_DESKTOP_SIZE
}

export const UA = navigator.userAgent || navigator.vendor || window.opera
const parser = new UAParser(UA)

export const { name: OS_NAME, version: OS_VERSION } = parser.getOS()
export const { name: BROWSER, version: BROWSER_VERSION } = parser.getBrowser()
export const {
  type: DEVICE_TYPE,
  model: DEVICE_MODEL,
  vendor: DEVICE_VENDOR,
} = parser.getDevice()

export const isContactsSupported = () =>
  'contacts' in navigator && 'ContactsManager' in window

export const IS_IOS =
  OS_NAME === 'iOS' ||
  (window.orientation !== undefined && OS_NAME === 'Mac OS') // for iPadOS

export const IS_SAFARI = BROWSER === 'Safari' || BROWSER === 'Mobile Safari'
export const IS_CHROME = BROWSER === 'Chrome'

export const isPhoneOrTablet =
  IS_IOS || DEVICE_TYPE === 'mobile' || DEVICE_TYPE === 'tablet'

export const isAndroid = OS_NAME === 'Android'
// Reference: https://stackoverflow.com/a/52855084
export const isTouchAvailable = window.matchMedia('(pointer: coarse)').matches

export const isInTwa = (() => {
  try {
    const wasTwa = JSON.parse(sessionStorageGetItem(SessionStorageKeys.IS_TWA))
    if (wasTwa != null) return wasTwa
    // eslint-disable-next-line no-empty
  } catch (e) {}

  console.log('window.location: ', window.location)
  const params = new URLSearchParams(window.location.search)

  console.log({ params })
  const twaPort = params.get('twa_port')
  if (twaPort) sessionStorageSetItem(SessionStorageKeys.TWA_PORT, twaPort)

  const twaWsPort = params.get('twa_ws_port')
  const twaWsToken = params.get('twa_ws_token') || ''
  if (twaWsPort) {
    sessionStorageSetItem(
      SessionStorageKeys.TWA_WS_URL,
      `ws://127.0.0.1:${twaWsPort}/${twaWsToken}`,
    )
  }

  const isTWA =
    document.referrer.includes('android-app://') ||
    params.get('utm_source') === 'twa' ||
    params.get('twa_version') != null
  sessionStorageSetItem(SessionStorageKeys.IS_TWA, isTWA)

  if (isTWA) LocalInsert('twaVersion', parseInt(params.get('twa_version'), 10))
  return isTWA
})()

export const isInWebView = (() => {
  try {
    const wasWebView = JSON.parse(
      sessionStorageGetItem(SessionStorageKeys.IS_WEBVIEW),
    )
    if (wasWebView != null) return wasWebView
    // eslint-disable-next-line no-empty
  } catch (e) {}

  const params = new URLSearchParams(window.location.search)

  const isWebview = params.get('isWebView') === 'true'
  sessionStorageSetItem(SessionStorageKeys.IS_WEBVIEW, isWebview)

  return isWebview
})()

export const isAndroidApp = isInTwa || isInWebView

export const getInstalledTwaVersion = () => LocalFind('twaVersion')

export const twaVersion = (() => {
  const params = new URLSearchParams(window.location.search)
  return params.get('twa_version')
})()

export const isInPWA = (() => {
  // Android and iOS 11.3+
  // replace standalone with fullscreen or minimal-ui according to your manifest
  if (matchMedia('(display-mode: standalone)').matches) return true

  // useful for iOS < 11.3
  if ('standalone' in navigator) return navigator.standalone

  // check if the app is launched via TWA
  return isInTwa
})()

export const nativeScrollbarWidth = (function getScrollbarWidth() {
  // Creating invisible container
  const outer = document.createElement('div')
  outer.style.visibility = 'hidden'
  outer.style.overflow = 'scroll' // forcing scrollbar to appear
  outer.style.msOverflowStyle = 'scrollbar' // needed for WinJS apps
  outer.classList.add(CUSTOM_SCROLL_CLASS)
  document.body.appendChild(outer)

  // Creating inner element and placing it in the container
  const inner = document.createElement('div')
  outer.appendChild(inner)

  // Calculating difference between container's full width and the child width
  const scrollbarWidth = outer.offsetWidth - inner.offsetWidth

  // Removing temporary elements from the DOM
  outer.parentNode.removeChild(outer)

  return scrollbarWidth
})()

// https://developer.mozilla.org/en-US/docs/Web/API/Window/navigator
/*
  "Mozilla/5.0 (X11; Ubuntu; Linux x86_64; rv:61.0) Gecko/20100101 Firefox/61.0"
  if (UA.indexOf('Firefox') > -1) return 'Firefox'

  "Mozilla/5.0 (Linux; Android 9; SAMSUNG SM-G955F Build/PPR1.180610.011) AppleWebKit/537.36 (KHTML, like Gecko) SamsungBrowser/9.4 Chrome/67.0.3396.87 Mobile Safari/537.36
  if (UA.indexOf('SamsungBrowser') > -1) return 'Samsung Internet'

  "Mozilla/5.0 (Macintosh; Intel Mac OS X 10_14_0) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/70.0.3538.102 Safari/537.36 OPR/57.0.3098.106"
  if (UA.indexOf('Opera') > -1 || UA.indexOf('OPR') > -1) return 'Opera'

  "Mozilla/5.0 (Windows NT 10.0; WOW64; Trident/7.0; .NET4.0C; .NET4.0E; Zoom 3.6.0; wbx 1.0.0; rv:11.0) like Gecko"
  if (UA.indexOf('Trident') > -1) return 'IE'

  "Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/58.0.3029.110 Safari/537.36 Edge/16.16299"
  // if (UA.indexOf('Edge') > -1) return 'MS Edge'

  "Mozilla/5.0 (X11; Linux x86_64) AppleWebKit/537.36 (KHTML, like Gecko) Ubuntu Chromium/66.0.3359.181 Chrome/66.0.3359.181 Safari/537.36"
  if (UA.indexOf('Chromium') > -1) return 'Chromium'

  "Mozilla/5.0 (X11; Linux x86_64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/76.0.3809.100 Safari/537.36"
  if (UA.indexOf('Chrome') > -1) return 'Chrome'

  "Mozilla/5.0 (iPhone; CPU iPhone OS 11_4 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/11.0 Mobile/15E148 Safari/604.1 980x1306"
  if (UA.indexOf('Safari') > -1) return 'Safari'

  if (browserName.startsWith('Chrome')) return 'Chrome'
  if (browserName.endsWith('Safari')) return 'Safari'
  if (browserName.startsWith('Opera')) return 'Opera'

  Replace all of below with browserName
  if (browserName == 'Chromium') return 'Chromium'
  if (browserName == 'IE') return 'IE'
  if (browserName == 'Edge') return 'MS Edge'
  if (browserName == 'Samsung Browser') return 'Samsung Browser'
  if (browserName == 'Firefox') return 'Firefox'
*/

export const isNotSupportedBrowser = ['Chrome WebView', 'Instagram'].includes(
  BROWSER,
)

export const IS_STORY_ENABLED = import.meta.env.VITE_ENABLE_STORY === 'true'
export const SHOULD_CATEGORIZE_CHATS =
  import.meta.env.VITE_CATEGORIZE_CHATS === 'true'
export const CAN_SEND_LOCATION =
  import.meta.env.VITE_CAN_SEND_LOCATION === 'true'
export const CAN_SEND_CONTACT = import.meta.env.VITE_CAN_SEND_CONTACT === 'true'
export const CAN_SEND_WAPPS = import.meta.env.VITE_CAN_SEND_WAPPS === 'true'
export const SUPPORT_ACCOUNT_ID = import.meta.env.VITE_SUPPORT_ACCOUNT_ID
export const SHOW_USER_INFO_TO_SUPPORT =
  import.meta.env.VITE_SHOW_USER_INFO_TO_SUPPORT === 'true'
